import React from 'react';
import { Link } from 'react-router-dom';
import { BridgeTransaction, TransactionStatus } from '../../types/api';

interface RecentTransactionsProps {
  transactions: BridgeTransaction[];
}

const RecentTransactions: React.FC<RecentTransactionsProps> = ({ transactions }) => {
  const getStatusColor = (status: TransactionStatus) => {
    switch (status) {
      case TransactionStatus.EXECUTED:
        return 'bg-green-100 text-green-800';
      case TransactionStatus.APPROVED:
        return 'bg-blue-100 text-blue-800';
      case TransactionStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case TransactionStatus.FAILED:
        return 'bg-red-100 text-red-800';
      case TransactionStatus.TIMEOUT:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getChainName = (chainId: string) => {
    switch (chainId) {
      case '56': return 'BSC';
      case '97': return 'BSC-T';
      case '2020': return 'Ronin';
      case '2021': return 'Ronin-T';
      default: return `Chain ${chainId}`;
    }
  };

  const formatAmount = (amount: string) => {
    const num = parseFloat(amount);
    if (num < 1000) return num.toFixed(2);
    if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
    return `${(num / 1000000).toFixed(1)}M`;
  };

  const getTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp * 1000;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Recent Transactions</h3>
          <Link
            to="/transactions"
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            View all →
          </Link>
        </div>
      </div>
      
      <div className="divide-y divide-gray-200">
        {transactions.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            <div className="text-gray-400 mb-2">
              <svg className="mx-auto h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-sm">No recent transactions</p>
          </div>
        ) : (
          transactions.map((tx) => (
            <div key={tx.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(tx.status)}`}>
                      {tx.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getChainName(tx.srcChainId)} → {getChainName(tx.destChainId)}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {formatAmount((BigInt(tx.amount) / BigInt(10 ** 18)).toString())} PFVS
                  </p>
                  <p className="text-xs text-gray-500 font-mono truncate">
                    {tx.id}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm text-gray-500">
                    {getTimeAgo(tx.requestTimestamp)}
                  </p>
                  <Link
                    to={`/transactions/${tx.requestTxHash}`}
                    className="text-xs text-blue-600 hover:text-blue-500"
                  >
                    View details
                  </Link>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentTransactions; 